/**
 * /coach-meeting/admin — Founder-only roster for monthly coach meetings.
 * Live list grouped by date, with cancel.
 */
import React, { useEffect, useState } from 'react';
import { Calendar, Clock, User, Phone, Trash2, RefreshCw, Loader2, MessageSquare } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const TAG = 'text-[10px] font-black uppercase tracking-[0.28em]';

export default function CoachMeetingAdmin() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/coach-meeting/admin', { headers: authHeaders() });
      setBookings(data.bookings || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) toast.error('Admin login required — sign in via /academy/login first');
      else toast.error('Could not load bookings');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const cancel = async (id, name) => {
    if (!window.confirm(`Cancel ${name}'s meeting?`)) return;
    try {
      await api.delete(`/coach-meeting/admin/${id}`, { headers: authHeaders() });
      setBookings(bookings.filter((b) => b.id !== id));
      toast.success('Cancelled');
    } catch { toast.error('Cancel failed'); }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F4F1EA] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-700" /></div>;
  }

  const grouped = bookings.reduce((acc, b) => {
    (acc[b.date] ||= { day_label: b.day_label, items: [] }).items.push(b);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort();

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-slate-900">
      <div className="max-w-4xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className={TAG + ' text-amber-700 mb-1'}>Coach Meetings · Roster</div>
            <h1 className="text-3xl font-black">Monthly parent check-ins</h1>
            <p className="text-slate-600 text-sm mt-1">{bookings.length} booking{bookings.length === 1 ? '' : 's'} on the calendar</p>
          </div>
          <button
            onClick={() => { setRefreshing(true); load(); }}
            className="flex items-center gap-2 text-xs uppercase tracking-widest font-black px-3 py-2 border border-slate-300 bg-white hover:border-amber-700 rounded"
            data-testid="coach-admin-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {bookings.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-slate-300 bg-white rounded">
            <Calendar className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No bookings yet. Parents land on <span className="font-semibold text-amber-700">/coach-meeting</span> and the first click lands here.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {dates.map((date) => (
              <div key={date} className="bg-white border border-slate-200 rounded overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5">
                  <div className={TAG + ' text-slate-500'}>{grouped[date].day_label}</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {grouped[date].items.sort((a, b) => a.time.localeCompare(b.time)).map((b) => (
                    <div key={b.id} className="px-4 py-4 flex items-start gap-4" data-testid={`coach-row-${b.id}`}>
                      <div className="flex-shrink-0 w-20 text-center">
                        <div className="text-[10px] font-black uppercase tracking-wider text-amber-700">{b.slot_label}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <User className="w-3.5 h-3.5 text-slate-400" />
                          <span className="font-bold text-slate-900">{b.parent_name}</span>
                          <span className="text-xs text-slate-500">· {b.parent_email}</span>
                        </div>
                        <div className="text-sm text-slate-700 mb-1">Scholar: <strong>{b.student_name}</strong></div>
                        {b.phone && (
                          <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Phone className="w-3 h-3" /> {b.phone}
                          </div>
                        )}
                        {b.note && (
                          <div className="flex items-start gap-1.5 text-xs text-slate-600 mt-2 bg-slate-50 border-l-2 border-amber-700 px-2 py-1.5">
                            <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>{b.note}</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => cancel(b.id, b.parent_name)}
                        className="text-rose-500 hover:text-rose-700 p-1"
                        data-testid={`coach-cancel-${b.id}`}
                        aria-label="Cancel"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 text-xs text-slate-500 border-t border-slate-200 pt-4">
          <strong className="text-slate-700">Heads up:</strong> You and Margaret are BCC'd on every parent confirmation,
          so each booking also lands directly in your inbox the moment it's made.
        </div>
      </div>
    </div>
  );
}
